// Basic
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
// Store and actions
import useRootOrganizationStore from '@/app/store/rootOrganization';
// Libs
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
// Components
import Entity from '@/common-utils/components/renders/entity/Entity';
import Info from '@/common-utils/components/renders/info/Info';
import Select from '@/common-utils/components/ui/select';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/common-utils/components/ui/form';
import { Input } from '@/common-utils/components/ui/input';
// Common
import { getOrganizationTypeOptions } from '../../../utils/getAvailableOrganizationTypes';
import ORGANIZATION_TYPES from '../../../enums/OrganizationTypes';
// Lang
import { nsOrganization } from '@/app/i18n/namespaces';

/**
 * @param root0
 * @param root0.registrationNumber
 * @param root0.setOrganizationFormData
 * @param root0.organizationData
 * @returns {JSX} FormAddOrganization
 */
function FormAddOrganization({
    registrationNumber,
    setOrganizationFormData,
    organizationData
}) {

    // Store
    const { type: organizationType } = useRootOrganizationStore(s => s.rootOrganization);

    // Constants
    const organizationTypeOptions = getOrganizationTypeOptions(organizationType);

    // Add organization form
    const addOrganizationFormSchema = z.object({
        registrationNumber: z.string(),
        legalName: z.string().nonempty(),
        type: z.enum(Object.keys(ORGANIZATION_TYPES)),
        clientCode: z.string().optional(),
        comment: z.string().optional()
    });
    const addOrganizationForm = useForm({
        resolver: zodResolver(addOrganizationFormSchema),
        mode: 'onChange',
        defaultValues: {
            registrationNumber: registrationNumber,
            legalName: '',
            type: undefined,
            clientCode: '',
            comment: ''
        }
    });

    // Hooks
    useEffect(() => {
        setOrganizationFormData({ data: addOrganizationForm.getValues(), isValid: addOrganizationForm.formState.isValid });
    }, [addOrganizationForm.watch(), addOrganizationForm.formState]);

    useEffect(() => {
        if (organizationData?.companyLongName) {
            addOrganizationForm.setValue('legalName', organizationData?.companyLongName);
        }
    }, [organizationData]);

    return <Form {...addOrganizationForm}  >
        <div className='space-y-2'>
            {!organizationData &&
                <Info
                    entity='modal.add_organization.info'
                    namespace={nsOrganization}
                    className='justify-center pt-2 pb-2'
                    variant='info'
                />
            }
            <FormField
                name='registrationNumber'
                control={addOrganizationForm.control}
                render={({ field }) => (
                    <FormItem>
                        <FormLabel>
                            <Entity namespace={nsOrganization} entity='keys.registration_number' />
                        </FormLabel>
                        <FormControl>
                            <Input
                                {...field}
                                type='tel'
                                maxLength='14'
                                autoComplete='off'
                                disabled={true}
                            />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                )}
            />

            <FormField
                name='legalName'
                control={addOrganizationForm.control}
                render={({ field }) => (
                    <FormItem>
                        <FormLabel required={!organizationData?.companyLongName}>
                            <Entity namespace={nsOrganization} entity='keys.legal_name' />
                        </FormLabel>
                        <FormControl>
                            <Input
                                {...field}
                                type='text'
                                autoComplete='off'
                                disabled={organizationData?.companyLongName}
                            />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                )}
            />
            <FormField
                name='type'
                control={addOrganizationForm.control}
                render={({ field }) => (
                    <FormItem>
                        <FormLabel required>
                            <Entity namespace={nsOrganization} entity='keys.type' />
                        </FormLabel>
                        <FormControl>
                            <Select
                                {...field}
                                options={organizationTypeOptions}
                                namespace={nsOrganization}
                                prefixEntity={'type.'}
                            />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                )}
            />
            <FormField
                name='clientCode'
                control={addOrganizationForm.control}
                render={({ field }) => (
                    <FormItem>
                        <FormLabel>
                            <Entity namespace={nsOrganization} entity='keys.client_code' />
                        </FormLabel>
                        <FormControl>
                            <Input
                                {...field}
                                type='text'
                                autoComplete='off'
                            />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                )}
            />
            <FormField
                name='comment'
                control={addOrganizationForm.control}
                render={({ field }) => (
                    <FormItem>
                        <FormLabel>
                            <Entity namespace={nsOrganization} entity='keys.comment' />
                        </FormLabel>
                        <FormControl>
                            <Input
                                {...field}
                                type='textarea'
                                autoComplete='off'
                            />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                )}
            />
        </div>
    </Form >;
}

FormAddOrganization.propTypes = {
    registrationNumber: PropTypes.string,
    setOrganizationFormData: PropTypes.func,
    organizationData: PropTypes.object
};

export default FormAddOrganization;